import { AllEnums, Enum } from '~/data/enums';

import {
  CustomFieldValues,
  EditCustomField,
  FieldAttributes,
  FieldAttributesHash,
  InputProps,
} from '../types';

import { filterEnumsByCountry, jsNumber, sortEnumLables } from './common';

export const jsEditCustomFieldData = ({
  strEntity,
  customFieldAttributesHash,
  customFields,
  legalEntityCountry,
  allEnums,
  customNamePrefix = '',
  init,
}: {
  strEntity: string; //FOR EXAMPLE, EMPLOYEE OR BILLABLE_HOURS_PROPOSAL,
  customFieldAttributesHash: FieldAttributesHash; //CUSTOM DATA ATTRIBUTES HASHED (ATTRIBUTE NAME AS KEY)
  customFields?: CustomFieldValues; //THESE ARE THE VALUES
  legalEntityCountry: string;
  allEnums: AllEnums;
  customNamePrefix?: string;
  init?: boolean;
}): EditCustomField[] => {
  const fieldAttributesHash: FieldAttributesHash =
    init === true ? {} : customFieldAttributesHash;
  const labels = filterEnumsByCountry(
    allEnums[`CUSTOM_ENTITY_${strEntity}`],
    '',
    legalEntityCountry,
  );
  let sortedLabels = labels.sort(sortEnumLables);
  return sortedLabels.map((label: Enum) => {
    const info = label.details ? label.details : '';
    const field: FieldAttributes = fieldAttributesHash[label.code];
    return {
      name: label.name,
      info,
      inputProps: {
        ...jsCustomFieldEditMode({
          customField: field,
          customFieldValues: customFields,
          allEnums,
          customNamePrefix,
        }),
      },
      type: field.fCustomDataAttributeDataType,
    };
  });
};

function jsCustomFieldEditMode({
  customField,
  customFieldValues,
  allEnums,
  customNamePrefix,
}: {
  customField: FieldAttributes;
  customFieldValues?: CustomFieldValues;
  allEnums: AllEnums;
  customNamePrefix?: string;
}): InputProps {
  if (!customField) {
    return { name: '', defaultValue: '' };
  }
  const customFieldAttributeName = customField.fCustomDataAttributeName;
  const strName = `${customNamePrefix}${customField.fCustomDataAttributeName}`;
  const strType = customField.fCustomDataAttributeDataType;
  const strValue = !customFieldValues
    ? undefined
    : customFieldValues.hasOwnProperty(customFieldAttributeName)
    ? customFieldValues[customFieldAttributeName]
    : '';
  switch (strType) {
    case 'STRING':
      return {
        validationRules: { maxLength: 80 },
        type: 'text',
        id: strName,
        name: strName,
        ...(strValue && { defaultValue: strValue }),
      };
    case 'DATE':
      return {
        id: strName,
        name: strName,
        ...(strValue && { defaultValue: strValue }),
      };
    case 'BOOLEAN':
      return {
        type: 'checkbox',
        id: strName,
        name: strName,
        ...(strValue && {
          defaultValue: strValue === 'true' ? 'true' : 'false',
        }),
      };
    case 'NUMBER':
      return {
        type: 'text',
        id: strName,
        validationRules: {
          pattern: {
            value: /^-?\d*[,.]?\d*$/,
            message: 'laValueNotValidNumber',
          },
        },
        name: strName,
        ...(strValue && {
          defaultValue:
            jsNumber(parseFloat(`${strValue}`)) !== 'NaN'
              ? jsNumber(parseFloat(`${strValue}`))
              : '',
        }),
      };
    case 'TEXT':
      return {
        validationRules: { maxLength: 3000 },
        id: strName,
        name: strName,
        ...(strValue && { defaultValue: strValue }),
      };
    case 'ENUM':
      return {
        name: strName,
        ...(strValue && { defaultValue: strValue }),
        options: allEnums[`${customField.fCustomDataAttributeDataSubtype}`],
      };
    case 'MULTISELECT':
      return {
        name: strName,
        ...(strValue && { defaultValue: strValue || [] }),
        options: allEnums[`${customField.fCustomDataAttributeDataSubtype}`],
      };
    default:
      return { name: '', defaultValue: '' };
  }
}
